<script lang="ts">
export default {
  name: 'EditTowerModal'
};
</script>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import AppButton from '@/components/v2/AppButton.vue';
import z, { SafeParseError, ZodFormattedError } from 'zod';
import { towerService, UpdateTowerInfoRequest } from '@/services/v2/TowerService';
import { ProgramTower } from '@/types/tower/v2/Tower';

const props = defineProps<{
  towerId?: string;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'update', success: boolean): void;
}>();

const formSchema = z.object({
  versionLabel: z.string().trim().min(1).max(100),
});

type FormSchemeType = z.infer<typeof formSchema>;

type ValidFormFields = {
  [K in keyof FormSchemeType]: boolean;
};

const defaultValidFormFields: ValidFormFields = {
  versionLabel: true,
};

class EditTowerForm {
  versionLabel?: string;
}

// ----------------
// refs and consts
// ----------------
const form = ref<EditTowerForm>(new EditTowerForm());
const formErrors = ref<ZodFormattedError<FormSchemeType>>();
const validFormFields = ref<ValidFormFields>(structuredClone(defaultValidFormFields));
const loading = ref(false);
const towerData = ref<ProgramTower>();

// ---------
// lifecycles
// ---------
onMounted(async () => {
  if (!props.towerId) {
    console.error('towerId is undefined');
    return;
  }
  
  addEventListener('keyup', handleCloseWithEscapeKey);
  towerData.value = await towerService.getTowerByTowerId(props.towerId);
  
  form.value.versionLabel = towerData.value?.versionLabel;
});

onUnmounted(() => {
  removeEventListener('keyup', handleCloseWithEscapeKey);
});

// ---------
// computed
// ---------
const formHasChanges = computed(() => {
  return towerData.value?.versionLabel?.trim() !== form.value.versionLabel?.trim();
});

// ---------
// handlers
// ---------
function handleCloseWithEscapeKey(e: KeyboardEvent): void {
  if (e.key === 'Escape') {
    handleClose();
  }
}

function handleClose(): void {
  emit('close');
}

async function handleUpdate(): Promise<void> {
  let response = false;
  
  try {
    // validate the form
    if (!validateForm()) {
      console.error('Failed to update tower');
      return;
    }
    
    const tower: UpdateTowerInfoRequest = {
      versionLabel: form.value.versionLabel
    };
    
    // save the tower
    if (towerData.value?.towerId) {
      response = await towerService.updateTowerInfo(towerData.value?.towerId, tower);
    } else {
      console.error('towerId is undefined');
    }
    
    emit('update', response);
  } catch (e) {
    console.error('tower update error', e);
    emit('update', response);
  }
}

// private functions
function validateForm(): boolean {
  const valid = formSchema.safeParse(form.value);
  validFormFields.value = structuredClone(defaultValidFormFields);
  
  if (!valid.success) {
    // set valid form fields to false
    formErrors.value = valid.error.format();
    
    for (const error of (valid as SafeParseError<FormSchemeType>).error.issues) {
      validFormFields.value[error.path[0].toString() as keyof ValidFormFields] = false;
    }
  }
  
  return valid.success;
}

</script>

<template>
  <dialog v-if="!loading" class="modal modal-open">
    <div class="modal-box flex flex-col gap-4 form-control">
      <div class="flex">
        <h1 class="self-center">Rename Tower Version</h1>
      </div>
      
      <!-- Version Name -->
      <div class="w-full">
        <label class="label">
          <span class="label-text text-xs text-gray-500">Version Name</span>
        </label>
        <input
          type="text"
          placeholder="e.g. Version with Side A"
          v-model="form.versionLabel"
          class="input input-md border border-solid w-full"
          :class="{ 'input-error' :  formErrors?.versionLabel?._errors }"
        />
        <label class="label">
            <span v-show="formErrors?.versionLabel?._errors"
                  class="label-text-alt text-xs text-red-500">
              {{ formErrors?.versionLabel?._errors.join(', ') }}
            </span>
        </label>
      </div>
      
      <div class="modal-action">
        <AppButton @click="handleClose">Cancel</AppButton>
        <AppButton
          :primary="true"
          class="text-white"
          @click="handleUpdate"
          :disabled="!formHasChanges"
        >
          Update
        </AppButton>
      </div>
    </div>
  </dialog>
</template>

<style>
</style>
